import { zodResolver } from '@hookform/resolvers/zod';
import React, { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import NavigationSection from '@ui/atoms/NavigationSection';

import TextArea from '@ui/forms/TextArea';
import TextInput from '@ui/forms/TextInput';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';
import SubmitButton from '@ui/molecules/SubmitButton';

import spaced from '@ui/styled/Spaced';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

import { schema } from './MemberVHCAnythingElseStep.data';

export interface IMemberVHCAnythingElseStepPageContent {
	heading: string;
	subheading: string;
	inputPlaceholder: string;
}

interface IMemberVHCAnythingElseStepProps extends IFlowStepComponentProps {
	pageContent: IMemberVHCAnythingElseStepPageContent;
}

const SpacedForm = spaced('form');

const MemberVHCAnythingElseStep: FC<IMemberVHCAnythingElseStepProps> = ({
	pageContent,
	existingFormData,
	handleFormSubmission = () => {},
	handleNavBack = () => {},
	stepKey,
}) => {
	const { heading, subheading, inputPlaceholder } = pageContent;
	const [submitting, setSubmitting] = useState(false);

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			...(existingFormData as unknown as Schema),
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		control,
		handleSubmit,
		register,
		formState: { errors, isSubmitted },
	} = form;

	const onSubmit = async (data: Record<string, unknown>) => {
		setSubmitting(true);
		await handleFormSubmission(data, stepKey);
		setSubmitting(false);
	};

	return (
		<CenteredGridItem>
			<FlexColumn gap="sm">
				<HeadingText>{heading}</HeadingText>
				<BasicText>{subheading}</BasicText>
			</FlexColumn>
			<SpacedForm marginTop="lg" marginBottom="xxxl" onSubmit={handleSubmit(onSubmit)}>
				<FlexColumn gap="xxl">
					<Controller
						name="anythingElse"
						control={control}
						render={({ field }) => (
							<TextArea
								{...field}
								label={heading}
								labelVariant="aria"
								placeholder={inputPlaceholder}
								error={isSubmitted && !!errors.anythingElse}
								errorLabel={!!errors?.anythingElse && errors?.anythingElse?.message}
							/>
						)}
					/>
					<NavigationSection>
						<Button label="Back" onClick={() => handleNavBack(stepKey)} />
						<SubmitButton variant="secondary" label="Next" submitting={submitting} />
					</NavigationSection>
				</FlexColumn>
			</SpacedForm>
		</CenteredGridItem>
	);
};

export default MemberVHCAnythingElseStep;
